// Header quick links (top-right). Home is always first and fixed; after it the
// user picks up to five links in Settings → Quick links. Defaults to SyQi
// (talk) + HRMEZ (capture), the two that were hard-coded before (Joel,
// 2026-10-18). Device-local, like the other small prefs.
import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Feather, Home, Settings as SettingsIcon, type LucideIcon } from 'lucide-react-native';
import { Butterfly } from '../v10/ButterflyIcon';
import { useV10 } from '../v10/theme';
import { TOOL_NAMES } from '../domain/olmps';
import { iconForTool } from './toolIcons';

export const MAX_QUICK_LINKS = 5;
export type QuickLinkSlots = (string | null)[];
export const DEFAULT_QUICK_LINKS: QuickLinkSlots = ['syqi', 'hrmez', null, null, null];

/** 'syqi' = talk to SyQi, 'hrmez' = the capture overlay, 'settings' = Settings,
 *  any other id opens that tool. */
export type QuickLinkOption = { id: string; label: string };

// ORACLE/TELOS fold into ATLAS; ASTRS is the ASTRA editor alias.
const HIDDEN_TOOL_IDS = new Set(['oracle', 'telos', 'astrs', 'syqi', 'hrmez']);

export const QUICK_LINK_OPTIONS: QuickLinkOption[] = [
  { id: 'syqi', label: 'SyQi · talk' },
  { id: 'hrmez', label: 'HRMEZ · capture' },
  ...Object.keys(TOOL_NAMES)
    .filter((id) => !HIDDEN_TOOL_IDS.has(id))
    .map((id) => ({ id, label: TOOL_NAMES[id] }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  { id: 'settings', label: 'Settings' },
];
const VALID_IDS = new Set(QUICK_LINK_OPTIONS.map((o) => o.id));

export function quickLinkLabel(id: string): string {
  return QUICK_LINK_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

const STORAGE_KEY = 'olmps.quickLinks.v1';
let current: QuickLinkSlots = DEFAULT_QUICK_LINKS;
let loaded = false;
const listeners = new Set<(s: QuickLinkSlots) => void>();

function normalize(raw: unknown): QuickLinkSlots {
  if (!Array.isArray(raw)) return DEFAULT_QUICK_LINKS;
  const seen = new Set<string>();
  const out: QuickLinkSlots = [];
  for (let i = 0; i < MAX_QUICK_LINKS; i++) {
    const v = raw[i];
    if (typeof v === 'string' && VALID_IDS.has(v) && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    } else out.push(null);
  }
  return out;
}

function emit() {
  for (const l of listeners) l(current);
}

async function load() {
  if (loaded) return;
  loaded = true;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (raw) {
      current = normalize(JSON.parse(raw));
      emit();
    }
  } catch {
    /* keep defaults */
  }
}

export function setQuickLinks(next: QuickLinkSlots) {
  current = normalize(next);
  emit();
  void AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(current)).catch(() => {});
}

export function useQuickLinks(): QuickLinkSlots {
  const [slots, setSlots] = useState(current);
  useEffect(() => {
    listeners.add(setSlots);
    void load();
    setSlots(current);
    return () => {
      listeners.delete(setSlots);
    };
  }, []);
  return slots;
}

/** Home + the user's chosen links, rendered as round header buttons. Shrinks
 *  the buttons once there are more than four so the header still fits a phone. */
export function QuickLinkButtons({
  onHome,
  onTalk,
  onCapture,
  onOpenTool,
  onOpenSettings,
  buttonStyle,
  hitSlop = 6,
}: {
  onHome: () => void;
  onTalk: () => void;
  onCapture: () => void;
  onOpenTool: (toolId: string) => void;
  onOpenSettings: () => void;
  buttonStyle: object;
  hitSlop?: number;
}) {
  const t = useV10();
  const links = useQuickLinks().filter((id): id is string => !!id);
  const compact = links.length + 1 > 4;
  const size = compact ? 32 : 38;
  const iconSize = compact ? 16 : 18;
  const sizeStyle = { width: size, height: size, borderRadius: size / 2 };

  const press = (id: string) => {
    if (id === 'syqi') onTalk();
    else if (id === 'hrmez') onCapture();
    else if (id === 'settings') onOpenSettings();
    else onOpenTool(id);
  };
  const icon = (id: string) => {
    if (id === 'syqi') return <Butterfly size={iconSize} color={t.goldBright} />;
    if (id === 'hrmez') return <Feather size={iconSize} color={t.goldBright} />;
    const Icon: LucideIcon = id === 'settings' ? SettingsIcon : iconForTool(id);
    return <Icon size={iconSize} color={t.goldBright} />;
  };

  return (
    <View pointerEvents="box-none" style={[styles.row, { gap: compact ? 4 : 6 }]}>
      <Pressable onPress={onHome} style={[buttonStyle, sizeStyle]} hitSlop={hitSlop} accessibilityLabel="Go to the dashboard">
        <Home size={iconSize} color={t.goldBright} />
      </Pressable>
      {links.map((id) => (
        <Pressable key={id} onPress={() => press(id)} style={[buttonStyle, sizeStyle]} hitSlop={hitSlop} accessibilityLabel={quickLinkLabel(id)}>
          {icon(id)}
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
});
